import * as React from 'react';
import { Image, FlatList, Text, View, StyleSheet, Platform, Pressable, useWindowDimensions } from 'react-native';
import * as AppUtilities from "../context/AppUtilities";
import * as AppConstants from "../context/AppConstants";
import { AppContext } from "../context/AppContext";
import { useSelector } from 'react-redux';
import { Divider } from 'react-native-paper';
import { SearchLauncher } from '../components/SearchLauncher';
import * as AppCache from "../context/AppCache";

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    padding: 20,
  },
  heading: {
    fontSize: 18,
    fontWeight: "bold",
  },
  label: {
    fontSize: 14,
    fontWeight: "bold",
    paddingRight: 5,
    flexWrap: "wrap",
  },
  labelPadLeft: {
    fontSize: 18,
    fontWeight: "bold",
    paddingLeft: 10,
    paddingTop: 5,
  },
  normal: {
    fontSize: 14,
    paddingRight: 10,
    flexWrap: "wrap",
    flex: 1,
  },
  divider: {
    marginVertical: 15,
  },
  line: {
    flexDirection: "row",
    marginBottom: 15,
    flexShrink: 1,
  },
  line2: {
    flexDirection: "row",
    marginBottom: 25,
    marginTop: 10,
    flexShrink: 1,
  },
  line3: {
    flexDirection: "row",
    marginBottom: 15,
    flexShrink: 1,
    paddingLeft: 10,
  },
  listItemStyle: {
    paddingLeft: 5,
  },
  listItemLeftIcon: {
    marginRight: 0,
  },
  mainList: {
    flexGrow: 0,
  },
  kittemListItem: {
    backgroundColor: "transparent",
  },
  link: {
    color: "#0000EE",
    textDecorationLine: "underline",
  },
  subtitle: {
    paddingTop: 5,
    fontWeight: "600",
    color: "#6f6f6f",
  },
  legendWrapper: {
    flexDirection: "row",
    alignItems: "center",
  },
  legendTitle: {
    fontWeight: "bold",
    paddingLeft: 5,
    paddingBottom: 2,
  },
  legendSubtitle: {
    paddingLeft: 5,
  },
  listWrapper: {
    flexGrow: 1,
    padding: 10,
  },
  searchWrapper: {
    padding: 10,
    paddingTop: 0,
    minHeight: 75,
    //maxHeight: 300,
  },
  descriptionWrapper: {
    paddingHorizontal: 10,
  },
});

export function Explore({ route, navigation }) {
  const { showPermanentSnack, showTemporarySnack, hideSnack, getLoginState, showSpinner, hideSpinner } = React.useContext(AppContext);
  const [filteredProducts, setFilteredProducts] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const { width, height } = useWindowDimensions();
  const dataFilter = useSelector((state) => state.dataFilter);
  const [isFocused, setIsFocused] = React.useState(false);
  const lastProcessedDataFilterID = React.useRef(null);

  React.useEffect(() => {
    if (navigation) {
      return navigation.addListener("focus", () => {
        AppUtilities.info("Explore focus");
        AppUtilities.recordAppPageView("explore");
        setIsFocused(true);
      });
    }
  }, [navigation]);

  React.useEffect(() => {
    if (navigation) {
      return navigation.addListener("blur", () => {
        AppUtilities.info("Explore blur");
        setIsFocused(false);
      });
    }
  }, [navigation]);

  async function loadProducts() {
    try {
      showSpinner();
      hideSnack();
      setLoading(true);

      let exploreJSON = null;
      if (AppUtilities.isDefaultFilter(dataFilter)) {

        const exploreLink = AppUtilities.getDefaultFilterExploreLink(dataFilter);

        AppUtilities.info("Explore.loadProducts using default filter: " + exploreLink);

        // See if it's in the cache
        exploreJSON = await AppCache.getCachedDefaultData(exploreLink);

        if (!exploreJSON) {

          const response = await AppUtilities.fetchUnauthorized(
            "Explore.loadProducts",
            AppUtilities.getExploreLink(dataFilter),
            "GET",
          );
  
          if (response.body) {
            exploreJSON = response.body;
          }
        } else {
          AppUtilities.info("Explore.loadProducts found default filter in cache");
        }

        AppUtilities.info("Explore.loadProducts default filter load completed");
      } else {
        AppUtilities.info("Explore.loadProducts fetching explore products");

        const response = await AppUtilities.fetchUnauthorized(
          "Explore.loadProducts",
          AppUtilities.getExploreLink(dataFilter),
          "GET",
        );

        if (response.body) {
          exploreJSON = response.body;
        }
      }

      if (exploreJSON) {
        hideSpinner();
        lastProcessedDataFilterID.current = dataFilter[AppConstants.SYNTHESIZED_TYPES.QUERY_ID[AppConstants.TYPE_KEYS.URL_KEY]];

        /*if (exploreJSON.products.find(product => product.partiallyhumane)) {
          setAnyPartial(true);
        }*/

        let products = exploreJSON.products.map((product) => (
          AppUtilities.createItemRow(
            product,
            () => {
              navigation.navigate(
                AppConstants.SCREENS.PRODUCT_EXPLORE_DETAILS,
                {
                  productid: product.id,
                  explore: "show",
                }
              );
            },
            (props) => (product.partiallyhumane ? <Image source={require('../../assets/circle-half-full-grey-25.png')} style={AppConstants.STYLES.image25x25} /> : null),
          )
        ));

        setFilteredProducts(products);
        setLoading(false);
        AppUtilities.info("Explore.loadProducts finished getting explore products");
      } else {
        throw new Error("Result body not found");
      }
    } catch (e) {
      showPermanentSnack(AppUtilities.getErrors(e), "Retry", () => loadProducts());
      hideSpinner();
      AppUtilities.handleException(e, "Explore.loadProducts");
    }
  }

  React.useEffect(() => {
    if (dataFilter && isFocused && lastProcessedDataFilterID.current != dataFilter[AppConstants.SYNTHESIZED_TYPES.QUERY_ID[AppConstants.TYPE_KEYS.URL_KEY]]) {
      loadProducts();
    }
  }, [dataFilter, isFocused]);

  const renderItem = ({ item, index }) => {
    return AppUtilities.renderProductRow(item, index, width);
  };

  if (loading) {
    return null;
  }

  return (
    <FlatList
      style={styles.mainList}
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={styles.listWrapper}
      getItemLayout={AppUtilities.productListItemLayout}
      data={
        filteredProducts && filteredProducts.length > 0 ?
            filteredProducts
          :
            [ { i: "-1", title: filteredProducts ? "No matches" : "Loading..." } ]
      }
      ItemSeparatorComponent={Divider}
      renderItem={renderItem}
      ListHeaderComponent={(
        <>
          <Text style={styles.subtitle}>Deliverables besides frozen meat</Text>
          {
            filteredProducts ?
                <View style={{ padding: 10 }}>
                  <SearchLauncher floating={false} hideSearchLocation={true} />
                </View>
              :
                null
          }
        </>
      )}
      ListFooterComponent={(
        <>
          <View style={{ paddingTop: filteredProducts && filteredProducts.length > 1 ? 20 : 0 }}>
            <Text>{filteredProducts && filteredProducts.length > 1 ? "Results: " + filteredProducts.length : ""}</Text>
          </View>
          <Divider style={AppConstants.STYLES.marginVertical20} />
          <View style={styles.line3}>
            <Text selectable={true} style={styles.label}>* </Text>
            <Text selectable={true} style={styles.normal}>Availability varies by delivery location, season, or items may be out of stock.</Text>
          </View>
        </>
      )}
    />
  );
}
